/**
 * Editorial Authoring V2 — material gallery (BUILD 1A).
 *
 * Card grid of a material's spreads, styled after "Мои рецептуры" —
 * NOT the old technical row list from /admin/editorial. Deliberately
 * does not import BlocksList, MasterEditor, or any other component
 * from the legacy authoring UI; it only reads existing block data
 * (title/master_type/content) already returned by the existing
 * GET /admin/editorial/materials/:id/blocks endpoint.
 *
 * BUILD 1A also receives mediaRecords from the page shell so a card
 * can resolve saved content.media_ids into a real thumbnail after
 * reload without making its own media request.
 *
 * No eye / hide-show / duplicate / up-down toolbar here by design —
 * those belong to the old technical list, not this BUILD.
 */
import { findMediaUrl } from "@/lib/editorialMedia.mjs";
import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { initialStoryImage } from "@/lib/editorialStoryMediaBindingV2.mjs";
import { resolveSpreadThumbnailV2 } from "./editorialSpreadDisplayV2.mjs";

const MASTER_LABELS_V2 = {
  HERO: "Обложка",
  EDITORIAL_STORY: "Редакционная история",
  VISUAL_STORY: "Визуальная история",
  MACRO: "Макро-кадр",
  MACRO_SENSORY: "Макро / сенсорные ноты",
  PORTRAIT_DIALOGUE: "Портрет / диалог",
  PROCESS_STORY: "Технология",
  STEP_BY_STEP: "Пошагово",
  EDITORIAL_PAUSE: "Пауза",
  RECIPE: "Рецепт",
  KNOWLEDGE: "Знание",
  HISTORICAL_EDITORIAL: "Историческая справка",
  VIDEO: "Видео",
};

const ACTIVE_MASTER_TYPES = new Set([
  "HERO", "EDITORIAL_STORY", "VISUAL_STORY", "MACRO", "MACRO_SENSORY",
  "PORTRAIT_DIALOGUE", "RECIPE", "HISTORICAL_EDITORIAL", "KNOWLEDGE", "VIDEO",
]);

export function masterLabelV2(masterType) {
  return MASTER_LABELS_V2[masterType] || masterType;
}

function spreadTitleV2(block) {
  const c = block.content || {};
  return c.recipe?.title || c.headline || c.title || c.subtitle || masterLabelV2(block.master_type);
}

function spreadThumbnailV2(block, mediaRecords) {
  const content = block.content || {};
  if (block.master_type === "RECIPE" && content.recipe?.hero?.url) {
    return content.recipe.hero.url;
  }
  const firstMediaId = block.master_type === "EDITORIAL_STORY"
    ? initialStoryImage(content).mediaId
    : (Array.isArray(content.media_ids) ? content.media_ids[0] : null);
  const savedMediaThumb = findMediaUrl(mediaRecords, firstMediaId);
  if (savedMediaThumb) return savedMediaThumb;
  return resolveSpreadThumbnailV2(content) || null;
}

// Protected Story photos require authenticated blob transport after reopen.
// Keep the hook in a child component; never call hooks from a mapped gallery row.
function EditorialStoryGalleryThumbnailV2({ url }) {
  const src = useEditorialMediaUrl(url);
  return src ? <img src={src} alt="" className="h-full w-full object-cover" /> : null;
}

export function EditorialMaterialGalleryV2({ blocks, mediaRecords = [], onOpenBlock }) {
  const activeBlocks = (blocks || []).filter((b) => ACTIVE_MASTER_TYPES.has(b.master_type));

  if (activeBlocks.length === 0) {
    return (
      <div className="rounded-2xl border border-espresso/10 bg-white/60 p-10 text-center text-espresso/70"
        data-testid="editorial-v2-gallery-empty">
        <div className="font-serif text-2xl mb-2">Разворотов пока нет</div>
        <div className="text-sm">Они появятся здесь, как только материал будет создан.</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="editorial-v2-gallery">
      {activeBlocks.map((b) => {
        const thumb = spreadThumbnailV2(b, mediaRecords);
        const isRecipe = b.master_type === "RECIPE";
        const recipeHero = isRecipe ? b.content?.recipe?.hero : null;
        const recipeImageStyle = recipeHero?.url ? {
          objectPosition: `${(recipeHero.focal_x ?? 0.5) * 100}% ${(recipeHero.focal_y ?? 0.5) * 100}%`,
          transform: `scale(${recipeHero.zoom ?? 1})`,
          transformOrigin: `${(recipeHero.focal_x ?? 0.5) * 100}% ${(recipeHero.focal_y ?? 0.5) * 100}%`,
        } : undefined;
        return (
          <button
            key={b.id}
            type="button"
            onClick={() => onOpenBlock(b.id)}
            className="group flex flex-col overflow-hidden rounded-2xl border border-espresso/10 bg-white text-left shadow-sm transition hover:shadow-md"
            data-testid={`editorial-v2-card-${b.id}`}
          >
            <div
              className="w-full overflow-hidden bg-cream/70"
              style={{ aspectRatio: "3 / 4" }}
            >
              {thumb ? (
                b.master_type === "EDITORIAL_STORY" ? (
                  <EditorialStoryGalleryThumbnailV2 url={thumb} />
                ) : (
                  <img
                    src={thumb}
                    alt=""
                    className="h-full w-full object-cover"
                    style={recipeImageStyle}
                  />
                )
              ) : (
                <div className="flex h-full w-full items-center justify-center text-espresso/25">
                  <span className="text-[11px] uppercase tracking-widest">Без фото</span>
                </div>
              )}
            </div>
            <div className="flex-1 space-y-1 p-3">
              <div className="line-clamp-2 font-serif text-base leading-snug text-espresso">
                {spreadTitleV2(b)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-espresso/50">
                {masterLabelV2(b.master_type)}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
